
import { CanvasConst, CanvasTextStyle, IconWithGlowMap, NO_STICKER, parseOffset, RegionOffset } from 'src/model';
import { drawAsset, drawWithStyle } from '../image';
import { clearCanvas, getFinishIterator, setTextStyle } from '../canvas-util';
import { createCanvas, scaleDrawCoordinate } from 'src/util';

const {
    width: CanvasWidth,
    finishTypeCardWidth,
    stickerSize,
} = CanvasConst;

export const drawStarContent = async ({
    ctx,
    iconImage,
    globalScale,
    cardIcon,
    text,
    star,
    starList,
    starAlignment = 'auto',
    style,
    coordinate = CanvasConst.starBox,
    onStarDraw,
    loopStarFinish,
}: {
    ctx: CanvasRenderingContext2D | null | undefined,
    iconImage: HTMLCanvasElement | null | undefined,
    globalScale: number,
    cardIcon: string,
    text: string | null,
    star: string | number,
    starList: string[],
    starAlignment: string,
    style?: CanvasTextStyle,
    coordinate?: { x: number, y: number },
    onStarDraw: (coordinate: [number, number]) => Promise<void>,
    loopStarFinish?: ReturnType<typeof getFinishIterator>,
}) => {
    /** We filter out all undefined star only at the trail of the star list */
    const { x, y } = coordinate;
    const normalizedStarList = Array.isArray(starList)
        ? starList
        : [];
    const starWidth = 50;
    const starSpacing = 3.8;
    let normalizedStarCount = cardIcon === 'custom'
        ? normalizedStarList.length
        : typeof star === 'string'
            ? star === '' ? 0 : 1
            : typeof star === 'number' ? star : 0;
    let totalWidth = starWidth * normalizedStarCount + starSpacing * (normalizedStarCount - 1);
    const baseline = y;

    let alignment = 'right';
    if (['rank', 'negative-level'].includes(cardIcon)) alignment = 'left';
    alignment = starAlignment === 'auto'
        ? alignment
        : starAlignment;
    if (normalizedStarCount > 12) alignment = 'center';

    if (text && alignment === 'center') {
        normalizedStarCount = 0;
        totalWidth = 0;
    }

    /** Level / Rank 13 is center-aligned. */
    const leftEdge = alignment === 'center'
        ? x + (CanvasWidth + totalWidth) / 2
        : alignment === 'left'
            ? x + 86 + totalWidth
            : x + 729;

    let offset = 0 - (starWidth + starSpacing);

    if (ctx && text && cardIcon !== 'st') {
        const fontSize = 50;
        const resetShadow = setTextStyle({ ctx, ...style, globalScale });
        ctx.textAlign = alignment === 'left' || alignment === 'right'
            ? alignment
            : 'left';
        ctx.font = `bold ${fontSize}px RoGSanSrfStd-Bd`;
        const offset = alignment === 'center'
            ? ctx.measureText(text).width / -2
            : alignment === 'left'
                ? starSpacing
                : (starWidth + starSpacing * 2) * -1;
        ctx.fillText(text, leftEdge + offset, baseline + fontSize * 0.9);
        ctx.textAlign = 'left';
        resetShadow();
    }

    const {
        canvas: starCanvas,
        ctx: starCtx,
    } = createCanvas(CanvasWidth * globalScale, (baseline + starWidth) * globalScale);
    await Promise.all([...Array(normalizedStarCount)]
        .map(async (_, index) => {
            offset += (starWidth + starSpacing);
            /** We must factor alignment here, because presentation does not factor alignment */
            const cardIconIndex = normalizedStarCount - 1 - index;
            const cardIconName = cardIcon === 'custom'
                ? normalizedStarList[cardIconIndex]
                : cardIcon;
            const coordinate: [number, number] = [
                leftEdge - (starWidth + Math.ceil(offset)),
                baseline,
            ];
            const glowCoordinate: [number, number] = [
                leftEdge - (starWidth + Math.ceil(offset)) - 3,
                baseline - 3,
            ];
            if (IconWithGlowMap[cardIconName]) await drawAsset(starCtx, 'subfamily/subfamily-glow.png', ...glowCoordinate);
            if (cardIconName === 'user-defined' && starCtx && iconImage) {
                starCtx?.drawImage(
                    iconImage,
                    ...coordinate,
                    starWidth, starWidth,
                );
            } else {
                await drawAsset(starCtx, `subfamily/subfamily-${cardIconName}.png`, ...coordinate);
            }
            return await onStarDraw(coordinate);
        })
    );
    if (!ctx) return;
    if (loopStarFinish) {
        const {
            canvas: starFinishCanvas,
            ctx: starFinishCtx,
        } = createCanvas(CanvasWidth, (baseline + starWidth));
        starFinishCtx?.drawImage(starCanvas, 0, 0);
        await loopStarFinish(
            starFinishCtx,
            'art',
            async (finishType) => {
                return await drawAsset(
                    starFinishCtx,
                    `finish/finish-typeart-${finishType}.png`,
                    (CanvasWidth - finishTypeCardWidth) / 2, stickerSize,
                );
            },
        );
        if (starCtx) {
            starCtx.globalCompositeOperation = 'source-in';
            starCtx.drawImage(starFinishCanvas, 0, 0);
        }
        ctx.drawImage(starCanvas, 0, 0);
    } else {
        ctx.drawImage(starCanvas, 0, 0);
    }
};

export const drawSticker = async ({
    ctx,
    sticker,
    globalScale,
    x, y,
}: {
    ctx: CanvasRenderingContext2D | null | undefined,
    sticker: string,
    globalScale: number,
    x: number,
    y: number,
}) => {
    if (!clearCanvas(ctx)) return;

    if (sticker === NO_STICKER) return Promise.resolve();
    ctx.scale(globalScale, globalScale);
    await drawAsset(ctx, `sticker/sticker-${sticker.toLowerCase()}.png`, x, y);
    ctx.resetTransform();
    return;
};

/** Duel terminal mark and Speed card mark. */
export const drawPredefinedMark = async ({
    bordered,
    canvas,
    globalScale,
    isDuelTerminalCard,
    isLink,
    isPendulum,
    isSpeedCard,
    offsetData,
    textStyle,
    type,
}: {
    canvas: HTMLCanvasElement,
    bordered: boolean,
    globalScale: number,
    isDuelTerminalCard: boolean,
    isLink: boolean,
    isPendulum: boolean,
    isSpeedCard: boolean,
    offsetData: RegionOffset,
    textStyle?: CanvasTextStyle,
    type: string,
}) => {
    const ctx = canvas?.getContext('2d');

    if (!ctx) return;

    if (isDuelTerminalCard) {
        const coordinate: [number, number, number, number] = isPendulum
            ? [255, 1072, 175, 35]
            : isLink
                ? [151, 848, 216, 24]
                : [80, 843, 270, 30];

        await drawWithStyle(
            canvas,
            `text/text-duel-terminal-${type}${bordered ? '-bordered' : ''}.png`,
            ...scaleDrawCoordinate([
                coordinate[0] - offsetData.x,
                coordinate[1] - offsetData.y,
                coordinate[2],
                coordinate[3],
            ], globalScale),
            globalScale,
            textStyle?.shadowColor ? { ...textStyle, blur: 3, x: 0, y: 0 } : textStyle,
        );
    }
    if (isSpeedCard) {
        const coordinate: [number, number, number, number] = isPendulum
            ? [255, 1084, 176.4, 25]
            : isLink
                ? [151, 854, 215.6, 22]
                : [80, 850, 245, 25];

        await drawWithStyle(
            canvas,
            `text/text-speed-duel-${type}${bordered ? '-bordered' : ''}.png`,
            ...scaleDrawCoordinate([
                coordinate[0] - offsetData.x,
                coordinate[1] - offsetData.y,
                coordinate[2],
                coordinate[3],
            ], globalScale),
            globalScale,
            textStyle?.shadowColor ? { ...textStyle, blur: 3, x: 0, y: 0 } : textStyle,
        );
    }
};

export const drawLimitedEditionMark = async ({
    canvas,
    type,
    globalScale,
    isPendulum,
    isLink,
    isLegacyCard,
    widthOffset,
    bordered,
    textStyle,
}: {
    ctx: CanvasRenderingContext2D | null | undefined,
    canvas: HTMLCanvasElement,
    globalScale: number,
    type: string,
    isPendulum: boolean,
    isLink: boolean,
    isLegacyCard: boolean,
    /** When the creator text is too long, this mark must be compressed */
    widthOffset: number,
    bordered: boolean,
    textStyle?: CanvasTextStyle,
}) => {
    const coordinate: [number, number, number, number] = !isLegacyCard || isPendulum
        ? (isLink && isPendulum)
            ? [220, 1123, 150 - widthOffset / globalScale, 37]
            : [145, 1123, 240 - widthOffset / globalScale, 37]
        : isLink
            ? [151, 846, 216, 36]
            : [80, 843, 240, 40];

    await drawWithStyle(
        canvas,
        `text/text-limited-edition-${type}${bordered ? '-bordered' : ''}.png`,
        ...scaleDrawCoordinate(coordinate, globalScale),
        globalScale,
        textStyle?.shadowColor ? { ...textStyle, blur: 3, x: 0, y: 0 } : textStyle,
    );
};

export const drawLinkRatingText = async (
    canvas: HTMLCanvasElement,
    rating: string,
    style: CanvasTextStyle,
    coordinateOffset: string,
    showStatLabelFlag: boolean,
    globalScale: number,
) => {
    const ctx = canvas.getContext('2d');

    if (!ctx || typeof rating !== 'string') return;

    const sizeOffsetData = parseOffset(coordinateOffset, CanvasConst.effectBox);
    if (showStatLabelFlag) await drawWithStyle(
        canvas,
        'link/link-text.png',
        ...scaleDrawCoordinate([600 - sizeOffsetData.x, 1080 - sizeOffsetData.y, 120, 30], globalScale),
        globalScale,
        style,
    );
    const baseline = isNaN(Number(rating))
        ? 1106
        : 1105;
    const resetStyle = setTextStyle({ ctx, ...style, globalScale });
    ctx.textAlign = 'right';
    ctx.scale(1.2, 1);
    ctx.font = `bold ${24 * globalScale}px RoGSanSrfStd-Bd`;
    ctx.fillText(`${rating}`, (622.75 - sizeOffsetData.x / 1.2) * globalScale, (baseline - sizeOffsetData.y) * globalScale);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.textAlign = 'left';
    resetStyle();
};